import { cloneDeep } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TOOL_CONST } from '@/constant';
import { LABELING_TRANSLATION_KEY_MAPPER } from '@/constant/toolConstant';
import {
  useAcceptAllFrameQuery,
  useFrameSubmitQuery,
  useVerfiyModal,
} from '@/hook';
import useRejectModal from '@/hook/frame/useRejectModal';
import {
  AddBox,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  CheckCircleLine,
  Close,
  Done,
  LeftPanelClose,
  Send,
  Undo,
  VButton,
  VFlex,
  VStack,
  VTextButton,
  VTextarea,
  VTooltip,
  Verified,
  useVModal,
} from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useReviewStore,
  useTaskFrameStore,
  useToolMenuStore,
  useToolPermissionStore,
} from '@/store';
import type { BoxWithLabel } from '@/type';

import { useToolPlayControllerContext } from './ToolPlayController';

export const TempToolReviewCommentFormViewer = () => {
  const { t } = useTranslation();
  const { openConfirm } = useVModal();
  const { currentFrame } = useTaskFrameStore();
  const { permission } = useToolPermissionStore();
  const { comment, setComment } = useReviewStore();
  const { deselect, findByName } = useToolMenuStore();
  const { $toolPlayController, moveFrameSeq } = useToolPlayControllerContext();
  const { mutate: submit, isPending: isSubmitPending } = useFrameSubmitQuery();
  const { mutate: acceptAllFrame, isPending: isAcceptAllFramePending } =
    useAcceptAllFrameQuery();
  const { setReviewCommentVisible } = useReviewStore();
  const [errorMessage, setErrorMessage] = useState('');
  const frameSeq = useMemo(
    () => currentFrame?.frameSeq ?? -1,
    [currentFrame?.frameSeq],
  );
  const taskID = useMemo(
    () => +(currentFrame?.taskID ?? -1),
    [currentFrame?.taskID],
  );
  const { openVerifyModal } = useVerfiyModal({
    frameSeq,
    taskId: taskID,
  });
  const { openRejectModal } = useRejectModal({
    frameSeq,
    taskId: taskID,
    onSuccess: () => {
      setErrorMessage('');
    },
  });

  const submitFrame = () => {
    submit({
      pTaskID: taskID,
      pFrameSeq: frameSeq,
    });
  };

  const acceptAll = () => {
    acceptAllFrame({
      pTaskID: taskID,
    });
  };

  const onSubmitClick = () => {
    openConfirm({
      title: t('task.submitAction.hyperresonant'),
      message: () => (
        <VStack className="w-full" gap={0.25}>
          <div className="text-body-xl">
            {t('task.submitCurrentFrame.rosieresite')}
          </div>
          <div className="text-body-lg text-gray-600">
            {t('task.submitCurrentFrameDescription.lymphangioma')}
          </div>
        </VStack>
      ),
      cancelText: t('modal.cancelButtonText.intimidate'),
      confirmText: t('task.submitAction.hyperresonant'),
      onConfirm: () => submitFrame(),
    });
  };

  const onAcceptAllFrameClick = () => {
    openConfirm({
      title: t('review.acceptAllButton.radiotropism'),
      message: () => (
        <VStack className="w-full" gap={0.25}>
          <div className="text-body-xl">
            {t('review.acceptAllFrame.splanchnoblast')}
          </div>
          <div className="text-body-lg text-gray-600">
            {t('review.acceptAllFrameDescription.pomster')}
          </div>
        </VStack>
      ),
      cancelText: t('modal.cancelButtonText.intimidate'),
      confirmText: t('review.acceptAllButton.radiotropism'),
      onConfirm: () => acceptAll(),
    });
  };

  const onNextReviewClick = () =>
    currentFrame?.nextReviewFrame &&
    moveFrameSeq(currentFrame.nextReviewFrame.frameSeq);

  const onPrevReviewClick = () =>
    currentFrame?.prevReviewFrame &&
    moveFrameSeq(currentFrame.prevReviewFrame.frameSeq);

  return (
    <>
      <VStack
        className={
          'absolute top-0 left-0 w-[314px] overflow-hidden bg-white p-2 shadow-sm'
        }
        style={{
          bottom: `${$toolPlayController.current?.offsetHeight}px`,
        }}
        gap={0.5}
      >
        <VStack className="relative size-full overflow-hidden">
          <VStack className="w-full border-b border-gray-200 pb-2" gap={0.5}>
            <VFlex justify="between" items="center">
              <div className="text-body-lg font-bold text-blue-300">
                {t('tool.leftSideViewerTab.decorate')}
              </div>

              {permission.labelingReview.updatable && (
                <VFlex
                  gap={0.25}
                  className="rounded-[2px] bg-status-inReview/50 p-1"
                  items="center"
                >
                  <div className="size-2 rounded-full bg-status-inReview" />
                  <div className="text-body-md">
                    {t('tool.leftSideViewerTab.nonsubscriber')}
                  </div>
                </VFlex>
              )}
            </VFlex>

            <VTextarea
              autoFocus
              content={errorMessage}
              fluid
              readOnly={!permission.labelingReview.updatable}
              rows={8}
              value={comment}
              valid={!errorMessage.length}
              onChange={e => setComment(e.target.value)}
            />

            {permission.labelingReview.updatable && (
              <VFlex gap={0.5} items="center">
                <VButton
                  size="md"
                  variant="dangerOutline"
                  fluid
                  onClick={() => {
                    if (!comment.length) {
                      setErrorMessage(t('review.frameAprrove.scrimpiness'));
                      return;
                    }

                    openRejectModal();
                  }}
                >
                  <VFlex gap={0.25} items="center">
                    <Undo className="size-4" />
                    {t('review.rejectButtonText.pioneership')}
                  </VFlex>
                </VButton>
                <VButton size="md" fluid onClick={openVerifyModal}>
                  <VFlex gap={0.25} items="center">
                    <Verified className="size-4" />
                    {t('review.accpetButtonText.predisplacement')}
                  </VFlex>
                </VButton>
              </VFlex>
            )}

            {permission.labelingReview.readable && (
              <VFlex className="w-full px-px" justify="between">
                {/* TODO: 툴팁 render 수정 후 해당 코드 수정 */}
                <VTooltip
                  content={
                    "Only available for frames in 'submitted' or 'in review' status"
                  }
                >
                  <div>
                    <VTextButton
                      size="sm"
                      onClick={onPrevReviewClick}
                      disabled={!currentFrame?.prevReviewFrame}
                    >
                      <VFlex gap={0.25} items="center">
                        <ArrowBack className="size-[14px]" />
                        Prev
                      </VFlex>
                    </VTextButton>
                  </div>
                </VTooltip>

                <VTooltip
                  content={
                    "Only available for frames in 'submitted' or 'in review' status"
                  }
                >
                  <div>
                    <VTextButton
                      size="sm"
                      onClick={onNextReviewClick}
                      disabled={!currentFrame?.nextReviewFrame}
                    >
                      <VFlex gap={0.25} items="center">
                        <ArrowForward className="size-[14px]" />
                        Next
                      </VFlex>
                    </VTextButton>
                  </div>
                </VTooltip>
              </VFlex>
            )}
          </VStack>

          <ToolRejectResonBoxViewer />

          <VStack gap={0.5} className="w-full">
            {permission.labelingReview.acceptable &&
              currentFrame?.workState === 'Reviewing' && (
                <VButton
                  fluid
                  loading={isAcceptAllFramePending}
                  outlined
                  size="md"
                  variant="secondary"
                  onClick={onAcceptAllFrameClick}
                >
                  {t('review.allVerifyButtonText.elderwood')}
                </VButton>
              )}

            {permission.labeling.updatable &&
              currentFrame?.workState === 'Working' && (
                <VButton
                  fluid
                  loading={isSubmitPending}
                  outlined
                  size="md"
                  variant="secondary"
                  onClick={onSubmitClick}
                >
                  <Send />
                  {t('task.submitAction.hyperresonant')}
                </VButton>
              )}
          </VStack>
        </VStack>
      </VStack>

      <VTooltip content={t('review.pannelCloseTooltip.pledgeless')}>
        <div
          className="absolute top-0 left-[314px] rounded-r-sm bg-white p-1"
          role="button"
          onClick={() => {
            setReviewCommentVisible(false);
            deselect(findByName('reviewData')!.id);
          }}
        >
          <LeftPanelClose className="size-4" />
        </div>
      </VTooltip>
    </>
  );
};

const ToolRejectResonBoxViewer = () => {
  const { t } = useTranslation();
  const { currentFrame, isFrameDataLoading } = useTaskFrameStore();
  const { permission } = useToolPermissionStore();
  const { selectBox } = useLabelingBoxContext();
  const { getCurrentPcdIndexReviewBoxList, removeBox: removeReviewBox } =
    useReviewStore();
  const reviewBoxList = getCurrentPcdIndexReviewBoxList();
  const { getCurrentBoxList, setBoxList } = useLabelingBoxContext();

  const rejectReasonBoxList = useMemo(() => {
    return (
      reviewBoxList?.map(box => {
        return {
          ...box,
          index: getCurrentBoxList().findIndex(b => b.id === box.id) + 1,
        };
      }) ?? []
    );
  }, [reviewBoxList, reviewBoxList, getCurrentBoxList]);

  const isRejectedReasonBoxListExist = rejectReasonBoxList.length;

  const onConvertToWorkDataClick = (reviewBox: BoxWithLabel) => {
    const toConvertWorkingBox = cloneDeep(reviewBox);
    toConvertWorkingBox.workType = TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE.WORK;

    setBoxList([...getCurrentBoxList(), toConvertWorkingBox]);
  };

  const onConvertToReviewDataClick = (workBox: BoxWithLabel) => {
    const newBoxList = getCurrentBoxList().filter(box => box.id !== workBox.id);
    setBoxList(newBoxList);
  };

  const isConvertedWorkBox = (box: BoxWithLabel) => {
    return (
      reviewBoxList.find(b => b.id === box.id) &&
      getCurrentBoxList().find(b => b.id === box.id)
    );
  };

  const onConvertAllToReviewClick = () => {
    reviewBoxList
      .filter(b => !isConvertedWorkBox(b))
      .forEach(b => onConvertToWorkDataClick(b));
  };

  return (
    <VStack className="w-full flex-1 gap-[2px] overflow-hidden pt-2">
      <VFlex items="center" className="w-full" justify="between">
        <div className="text-body-md font-semibold">
          {t('review.feedbackBoxes.expressiveness')}
        </div>

        {permission.labeling.updatable &&
          currentFrame?.workState === 'Working' && (
            <VTextButton onClick={onConvertAllToReviewClick}>
              <VFlex gap={0.25} items="center" className="text-body-md">
                <Done className="size-[14px]" />
                <span>{t('review.convertAllToObject.aghast')}</span>
              </VFlex>
            </VTextButton>
          )}
      </VFlex>

      <VStack className="w-full flex-1 gap-[2px] overflow-y-auto">
        {!isRejectedReasonBoxListExist && (
          <VStack
            gap={0.25}
            className="w-full py-10 text-gray-300"
            items="center"
          >
            <AddBox className="size-8" />
            <div className="text-body-md">
              {t('review.noFeedbackItems.nivation')}
            </div>
          </VStack>
        )}
        {rejectReasonBoxList.map((box, index) => {
          const { Icon, onClick, iconClassName } = (() => {
            const isConvertedToWork = !!isConvertedWorkBox(box);

            if (
              currentFrame?.workState === 'Reviewing' &&
              permission.labelingReview.updatable
            )
              return {
                Icon: Close,
                onClick: isConvertedToWork
                  ? () => {}
                  : () => removeReviewBox(box.id),
                iconClassName: isConvertedToWork
                  ? 'hidden'
                  : 'size-4 text-gray-400',
              };

            if (
              currentFrame?.workState === 'Working' &&
              permission.labeling.updatable &&
              isConvertedToWork
            )
              return {
                Icon: CheckCircle,
                onClick: () => onConvertToReviewDataClick(box),
                iconClassName: 'size-4 text-success-500',
              };

            if (
              currentFrame?.workState === 'Working' &&
              permission.labeling.updatable &&
              !isConvertedToWork
            )
              return {
                Icon: CheckCircleLine,
                onClick: () => onConvertToWorkDataClick(box),
                iconClassName: 'size-4 text-primary-500',
              };

            return { Icon: null, onClick: null, iconClassName: '' };
          })();

          return (
            <VFlex gap={0.25} className="w-full" items="center" key={box.id}>
              <VFlex
                className="flex-1 rounded-[2px] border border-gray-200 px-2 py-1"
                gap={0.5}
                role="button"
                onClick={() => selectBox(box.id)}
              >
                <span className="text-body-md text-gray-500">#{index + 1}</span>
                <span className="text-body-md">
                  {t(
                    LABELING_TRANSLATION_KEY_MAPPER[
                      box.label as keyof typeof LABELING_TRANSLATION_KEY_MAPPER
                    ],
                  )}
                </span>
              </VFlex>

              {Icon && (
                <div
                  className={'p-[5px]'}
                  role="button"
                  onClick={() => !isFrameDataLoading && onClick?.()}
                >
                  <Icon className={iconClassName} />
                </div>
              )}
            </VFlex>
          );
        })}
      </VStack>
    </VStack>
  );
};
